import React from "react";
import {
    MDBBtn,
    MDBIcon,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
} from "mdb-react-ui-kit";

function SSOInformationGuide({ showModal, setShowModal }) {
    return (
        <MDBModal
            open={showModal}
            onClose={() => setShowModal(false)}
            tabIndex="-1"
            size="lg"
        >
            <MDBModalDialog size="lg">
                <MDBModalContent>
                    <MDBModalHeader>
                        <MDBModalTitle>Setting up Microsoft Entra ID Single Sign-On</MDBModalTitle>
                        <MDBBtn
                            className="btn-close"
                            color="none"
                            onClick={() => setShowModal(false)}
                        ></MDBBtn>
                    </MDBModalHeader>
                    <MDBModalBody>
                        <div className="sso-guide-content">
                            {/* Introduction */}
                            <div className="mb-4">
                                <p>
                                    Microsoft Entra ID (formerly Azure AD) Single Sign-On allows users to access your application
                                    using their organizational credentials. Follow these steps to configure SSO integration.
                                </p>
                            </div>

                            {/* Prerequisites */}
                            <div className="mb-4">
                                <h6 className="fw-bold text-primary mb-3">
                                    <MDBIcon icon="check-circle" className="me-2" />
                                    Prerequisites
                                </h6>
                                <ul className="list-unstyled ps-3">
                                    <li className="mb-2">
                                        <MDBIcon icon="chevron-right" className="me-2 text-muted" size="sm" />
                                        Microsoft Entra ID tenant with appropriate permissions
                                    </li>
                                    <li className="mb-2">
                                        <MDBIcon icon="chevron-right" className="me-2 text-muted" size="sm" />
                                        Application Administrator or Global Administrator role
                                    </li>
                                    <li className="mb-2">
                                        <MDBIcon icon="chevron-right" className="me-2 text-muted" size="sm" />
                                        Access to your application's admin panel
                                    </li>
                                </ul>
                            </div>

                            {/* Steps to Configure */}
                            <div className="mb-4">
                                <h6 className="fw-bold text-primary mb-3">
                                    <MDBIcon icon="cogs" className="me-2" />
                                    Steps to Configure SSO
                                </h6>

                                <div className="steps-container">
                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">1</span>
                                            <div>
                                                <strong>Download Metadata File</strong>
                                                <p className="mb-1 text-muted">
                                                    Click "Download" to get your application's metadata file. This file contains
                                                    configuration details needed for Microsoft Entra ID.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">2</span>
                                            <div>
                                                <strong>Configure in Microsoft Entra ID</strong>
                                                <p className="mb-1 text-muted">
                                                    Sign in to the{" "}
                                                    <a href="https://entra.microsoft.com/" target="_blank" rel="noopener noreferrer" className="text-primary">
                                                        Microsoft Entra admin center
                                                    </a>{" "}
                                                    with appropriate privileges.
                                                </p>
                                                <ul className="text-muted small mt-2 mb-1">
                                                    <li>Go to <strong>Enterprise applications</strong> → <strong>New application</strong></li>
                                                    <li>Select <strong>Create your own application</strong></li>
                                                    <li>Choose <strong>Integrate any other application you don't find in the gallery</strong></li>
                                                    <li>Upload the metadata file you downloaded in step 1</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">3</span>
                                            <div>
                                                <strong>Download Microsoft Entra ID Metadata</strong>
                                                <p className="mb-1 text-muted">
                                                    From your Microsoft Entra ID application:
                                                </p>
                                                <ul className="text-muted small mt-2 mb-1">
                                                    <li>Go to <strong>Single sign-on</strong> → <strong>SAML</strong></li>
                                                    <li>In section 3 "SAML Signing Certificate", download the <strong>Federation Metadata XML</strong></li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">4</span>
                                            <div>
                                                <strong>Upload to Your Application</strong>
                                                <p className="mb-1 text-muted">
                                                    Click "Upload" button and select the Federation Metadata XML file from Microsoft Entra ID.
                                                    This will automatically populate the required fields.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">5</span>
                                            <div>
                                                <strong>Verify Configuration</strong>
                                                <p className="mb-1 text-muted">
                                                    Ensure all fields are correctly populated:
                                                </p>
                                                <ul className="text-muted small mt-2 mb-1">
                                                    <li><strong>Entity ID:</strong> Should match your Microsoft Entra ID identifier</li>
                                                    <li><strong>Tenant ID:</strong> Your organization's unique identifier (GUID)</li>
                                                    <li><strong>SSO Endpoint:</strong> Microsoft's SAML sign-in URL</li>
                                                    <li><strong>Certificate:</strong> Valid signing certificate</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">6</span>
                                            <div>
                                                <strong>Choose Login Option</strong>
                                                <p className="mb-1 text-muted">
                                                    Select your preferred login behavior:
                                                </p>
                                                <ul className="text-muted small mt-2 mb-1">
                                                    <li><strong>Optional:</strong> Users can choose between SSO and regular login</li>
                                                    <li><strong>Required:</strong> Users must use SSO to access the application</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="step-item mb-3 p-3 border rounded">
                                        <div className="d-flex align-items-start">
                                            <span className="badge bg-primary rounded-pill me-3 mt-1">7</span>
                                            <div>
                                                <strong>Assign Users to the Streams.AI Application</strong>
                                                <p className="mb-1 text-muted">
                                                    Only assigned users in Microsoft Entra ID can log in using SSO.
                                                </p>
                                                <ul className="text-muted small mt-2 mb-1">
                                                    <li>Open the <strong>Streams.AI Enterprise Application</strong> in Entra</li>
                                                    <li>Go to <strong>Users and Groups</strong></li>
                                                    <li>Click <strong>Add user/group</strong></li>
                                                    <li>Select the users or groups who need SSO access</li>
                                                    <li>Click <strong>Assign</strong> to complete</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Important Notes */}
                            <div className="mb-4">
                                <h6 className="fw-bold text-warning mb-3">
                                    <MDBIcon icon="exclamation-triangle" className="me-2" />
                                    Important Notes
                                </h6>
                                <div className="alert alert-warning w-100">
                                    <ul className="mb-0">
                                        <li>Ensure your certificate is valid and not expired</li>
                                        <li>Test the SSO configuration with a test user before enabling for all users</li>
                                        <li>Keep backup admin access in case SSO configuration needs troubleshooting</li>
                                        <li>Users must exist in both your application and Microsoft Entra ID</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Troubleshooting */}
                            <div className="mb-4">
                                <h6 className="fw-bold text-info mb-3">
                                    <MDBIcon icon="tools" className="me-2" />
                                    Common Issues & Solutions
                                </h6>
                                <div className="accordion" id="troubleshootingAccordion">
                                    <div className="card">
                                        <div className="card-header p-2">
                                            <button
                                                className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target="#collapse1"
                                            >
                                                <small><strong>Certificate Validation Errors</strong></small>
                                            </button>
                                        </div>
                                        <div id="collapse1" className="collapse" data-bs-parent="#troubleshootingAccordion">
                                            <div className="card-body p-2">
                                                <small className="text-muted">
                                                    Ensure the certificate is properly formatted and not expired.
                                                    Re-download the metadata file from Microsoft Entra ID if needed.
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-header p-2">
                                            <button
                                                className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target="#collapse2"
                                            >
                                                <small><strong>Users Cannot Login</strong></small>
                                            </button>
                                        </div>
                                        <div id="collapse2" className="collapse" data-bs-parent="#troubleshootingAccordion">
                                            <div className="card-body p-2">
                                                <small className="text-muted">
                                                    Verify that users are assigned to the application in Microsoft Entra ID
                                                    and that their email addresses match between systems.
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="card-header p-2">
                                            <button
                                                className="btn btn-link text-start p-0 w-100 text-decoration-none"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target="#collapse3"
                                            >
                                                <small><strong>Invalid Entity ID or SSO Endpoint</strong></small>
                                            </button>
                                        </div>
                                        <div id="collapse3" className="collapse" data-bs-parent="#troubleshootingAccordion">
                                            <div className="card-body p-2">
                                                <small className="text-muted">
                                                    Double-check that the Tenant ID matches across Entity ID and SSO Endpoint URLs.
                                                    Both should contain the same GUID value.
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Support Links */}
                            <div className="mb-3">
                                <h6 className="fw-bold text-success mb-3">
                                    <MDBIcon icon="life-ring" className="me-2" />
                                    Additional Resources
                                </h6>
                                <ul className="list-unstyled">
                                    <li className="mb-2">
                                        <MDBIcon icon="external-link-alt" className="me-2 text-muted" size="sm" />
                                        <a href="https://docs.microsoft.com/en-us/azure/active-directory/saas-apps/tutorial-list" target="_blank" rel="noopener noreferrer" className="text-primary">
                                            Microsoft Entra ID Application Integration Tutorials
                                        </a>
                                    </li>
                                    <li className="mb-2">
                                        <MDBIcon icon="external-link-alt" className="me-2 text-muted" size="sm" />
                                        <a href="https://docs.microsoft.com/en-us/azure/active-directory/develop/single-sign-on-saml-protocol" target="_blank" rel="noopener noreferrer" className="text-primary">
                                            SAML Single Sign-On Protocol Documentation
                                        </a>
                                    </li>
                                    <li className="mb-2">
                                        <MDBIcon icon="external-link-alt" className="me-2 text-muted" size="sm" />
                                        <a href="https://entra.microsoft.com/" target="_blank" rel="noopener noreferrer" className="text-primary">
                                            Microsoft Entra Admin Center
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* Footer Note */}
                            <div className="alert alert-info mb-0">
                                <small>
                                    <MDBIcon icon="info-circle" className="me-2" />
                                    <strong>Tip:</strong> Save this configuration after uploading the metadata file.
                                    You can always modify the settings later if needed.
                                </small>
                            </div>
                        </div>
                    </MDBModalBody>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}

export default SSOInformationGuide;